/* eslint-disable no-irregular-whitespace */
import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Button from '@cloudscape-design/components/button';
import Flashbar, { FlashbarProps } from '@cloudscape-design/components/flashbar';
import { samples } from '../samples';

export const AboutTab = () => {
  const [flashMessages, setFlashMessages] = useState<FlashbarProps.MessageDefinition[]>([]);

  const copyToClipboard = async (sampleId: string, testName: string) => {
    try {
      const sample = samples[sampleId];
      if (!sample) {
        throw new Error('Sample not found');
      }
      
      await navigator.clipboard.writeText(sample.content);
      
      setFlashMessages([{
        type: 'success',
        content: `${testName} sample copied to clipboard! Switch to the "Analyze text on invisible Characters" tab and paste it into the text input.`,
        dismissible: true,
        onDismiss: () => setFlashMessages([]),
        id: 'copy-success'
      }]);

      // Auto-dismiss after 5 seconds
      setTimeout(() => setFlashMessages([]), 5000);
    } catch {
      setFlashMessages([{
        type: 'error',
        content: `Failed to copy sample to clipboard. Please try again or manually copy from the sample files.`,
        dismissible: true,
        onDismiss: () => setFlashMessages([]),
        id: 'copy-error'
      }]);

      setTimeout(() => setFlashMessages([]), 5000);
    }
  };

  return (
    <>
      <Flashbar items={flashMessages} />
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">About Invisible Characters</Header>}>
        <SpaceBetween size="m">
          <Box variant="p">
            Invisible characters are Unicode characters that have no visual representation but can affect text processing, 
            rendering, and security. This tool detects and analyzes various types of invisible characters and security threats 
            that can be hidden within text.
          </Box>
          
          <Alert type="info">
            <strong>Why This Matters:</strong> Invisible characters can be used for filter evasion, phishing attacks, 
            steganography, filename spoofing, and bypassing security controls. Understanding these threats helps protect 
            against sophisticated Unicode-based attacks.
          </Alert>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Security Tests & Detection Methods</Header>}>
        <SpaceBetween size="l">
          
          {/* 1. Basic Invisible Characters */}
          <ExpandableSection 
            headerText="1. Basic Invisible Character Detection" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('zero-width-space', 'Test 1')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects zero-width and formatting characters that are commonly used for steganography or text manipulation.
              </Box>
              
              <div>
                <Box variant="awsui-key-label">Detected Character Types:</Box>
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box variant="p"><Badge color="blue">ZWSP</Badge> Zero-Width Space (U+200B)</Box>
                    <Box variant="p"><Badge color="blue">ZWNJ</Badge> Zero-Width Non-Joiner (U+200C)</Box>
                    <Box variant="p"><Badge color="blue">ZWJ</Badge> Zero-Width Joiner (U+200D)</Box>
                    <Box variant="p"><Badge color="blue">ZWNBSP</Badge> Zero-Width No-Break Space (U+FEFF)</Box>
                  </div>
                  <div>
                    <Box variant="p"><Badge color="green">LRM</Badge> Left-to-Right Mark (U+200E)</Box>
                    <Box variant="p"><Badge color="green">RLM</Badge> Right-to-Left Mark (U+200F)</Box>
                    <Box variant="p"><Badge color="red">RLO</Badge> Right-to-Left Override (U+202E)</Box>
                    <Box variant="p"><Badge color="red">LRO</Badge> Left-to-Right Override (U+202D)</Box>
                  </div>
                </ColumnLayout>
              </div>

              <div style={{ backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example:</Box>
                <Box variant="code">
                  Text: "Hello​World" (contains ZWSP between words)<br/>
                  Detected: 1 invisible character at position 5
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="grey">LOW to MEDIUM</Badge> - Depends on context and quantity.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 2. Steganography Detection */}
          <ExpandableSection 
            headerText="2. Steganography & Hidden Data Encoding" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('repeating-pattern', 'Test 2')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects binary encoding patterns using zero-width characters to hide data within text (AI watermarking, 
                fingerprinting, covert communication).
              </Box>

              <div>
                <Box variant="awsui-key-label">Detection Methods:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Binary ZWSP/ZWNJ Encoding:</strong> ZWSP=0, ZWNJ=1</Box>
                  <Box variant="p">• <strong>ZWJ Sequences:</strong> Multiple Zero-Width Joiners in short text</Box>
                  <Box variant="p">• <strong>Space-based Encoding:</strong> Multiple different space character types</Box>
                  <Box variant="p">• <strong>Repeating Patterns:</strong> Regular intervals of invisible characters</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example - Binary Encoding:</Box>
                <Box variant="code">
                  Text: "Hello​‌​‌​​​‌World"<br/>
                  Binary: 01001000 (8 bits)<br/>
                  Decoded: 'H' (ASCII 72)<br/>
                  <br/>
                  Pattern: ZWSP=0, ZWNJ=1<br/>
                  ​ = 0, ‌ = 1, ​ = 0, ‌ = 1, ​ = 0, ​ = 0, ​ = 0, ‌ = 1
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">HIGH</Badge> - Often indicates intentional data hiding or tracking.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 3. Semantic Context Analysis */}
          <ExpandableSection 
            headerText="3. Semantic Context Analysis (Filter Evasion)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('semantic-context', 'Test 3')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects invisible characters embedded within words to bypass content filters, spam detection, or keyword blocking.
              </Box>

              <div>
                <Box variant="awsui-key-label">Common Evasion Targets:</Box>
                <ColumnLayout columns={3} variant="text-grid">
                  <Box variant="p">• Password, admin, hack</Box>
                  <Box variant="p">• V​iagra, c​asino, l​ottery</Box>
                  <Box variant="p">• Adult content keywords</Box>
                </ColumnLayout>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example - Word Splitting:</Box>
                <Box variant="code">
                  Original: "This password is secure"<br/>
                  Attack: "This pass​word is secure"<br/>
                  <br/>
                  Detection: Word "pass​word" split at position 9<br/>
                  Context: "...This pass​word is secure..."<br/>
                  <br/>
                  Result: Bypasses keyword filter looking for "password"
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to HIGH</Badge> - Indicates intentional filter bypass.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 4. URL/Domain Confusables */}
          <ExpandableSection 
            headerText="4. URL/Domain Confusables (Phishing)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('url-confusables', 'Test 4')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects URLs using punycode (IDN homograph attacks), TLD spoofing, and subdomain tricks for phishing.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detection Techniques:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Punycode Detection:</strong> xn-- domains (internationalized domain names)</Box>
                  <Box variant="p">• <strong>TLD Spoofing:</strong> Cyrillic/Greek characters that look like Latin</Box>
                  <Box variant="p">• <strong>Subdomain Tricks:</strong> user@domain patterns</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Punycode Attack:</strong><br/>
                    Displayed: аpple.com (Cyrillic 'а')<br/>
                    Actual: xn--pple-43d.com<br/>
                    Risk: Domain spoofing, looks identical to apple.com
                  </Box>
                  <Box variant="code">
                    <strong>2. TLD Spoofing:</strong><br/>
                    Displayed: paypal.сom (Cyrillic 'о' in .com)<br/>
                    Actual: paypal.xn--om-7kc<br/>
                    Risk: Fake TLD appears as legitimate .com
                  </Box>
                  <Box variant="code">
                    <strong>3. Subdomain Trick:</strong><br/>
                    URL: https://paypal.com@evil.com<br/>
                    Appears to be: paypal.com<br/>
                    Actually goes to: evil.com<br/>
                    Risk: Username portion before @ is ignored
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">HIGH</Badge> - Direct phishing threat, credential theft.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 5. BiDi Exploits */}
          <ExpandableSection 
            headerText="5. Bidirectional Text (BiDi) Exploits" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('bidi-exploits', 'Test 5')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects Right-to-Left Override (RLO) and Left-to-Right Override (LRO) characters that can reverse text 
                display, enabling filename spoofing and "Trojan Source" code injection attacks.
              </Box>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '2px solid #f44336' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>⚠️ CRITICAL EXAMPLE - Filename Spoofing:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>Attack:</strong><br/>
                    Filename: photo‮gpj.exe<br/>
                    (contains RLO character U+202E before "gpj")<br/>
                    <br/>
                    <strong>How it appears to user:</strong><br/>
                    Displayed: photoexe.jpg<br/>
                    <br/>
                    <strong>Actual file:</strong><br/>
                    Real extension: .exe (executable malware)<br/>
                    <br/>
                    <strong>Why it&apos;s dangerous:</strong><br/>
                    • User thinks they&apos;re opening an image (.jpg)<br/>
                    • Actually executes malicious code (.exe)<br/>
                    • RLO reverses text direction, hiding true extension
                  </Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example - Trojan Source Attack:</Box>
                  <Box variant="code">
                    Code: if (isAdmin) &#123; access = &quot;admin‮ &#125; ⁦ if (isAdmin)⁩ &#123; &quot;granted&quot;; &#125;<br/>
                    <br/>
                    Displayed: Normal if-statement logic<br/>
                    Actually: Hidden code execution path<br/>
                    <br/>
                    Risk: Source code displays differently than it executes
                  </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">CRITICAL</Badge> - Enables malware execution, code injection.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 6. Zero-Width Chains */}
          <ExpandableSection 
            headerText="6. Zero-Width Character Chains" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('zero-width-chains', 'Test 6')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects consecutive sequences of zero-width characters (chains) that indicate advanced steganography or 
                text manipulation beyond simple invisible character insertion.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detected Patterns:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Chain Length:</strong> 2+ consecutive zero-width characters</Box>
                  <Box variant="p">• <strong>Stacked Invisibles:</strong> Multiple ZW chars at same position</Box>
                  <Box variant="p">• <strong>Common Chains:</strong> ZWSP+ZWJ, ZWNJ+ZWSP+ZWJ, etc.</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example:</Box>
                <Box variant="code">
                  Text: "Secret​‌‍​‌Message"<br/>
                  <br/>
                  Detected Chain: ZWSP+ZWNJ+ZWJ+ZWSP+ZWNJ<br/>
                  Chain Length: 5 characters<br/>
                  Positions: [6, 7, 8, 9, 10]<br/>
                  <br/>
                  Pattern: ZWSP+ZWNJ+ZWJ+ZWSP+ZWNJ<br/>
                  Interpretation: Complex encoding scheme
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to HIGH</Badge> - Indicates sophisticated data encoding.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 7. Homoglyphs */}
          <ExpandableSection 
            headerText="7. Homoglyph & Confusable Characters" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('homoglyph-confusables', 'Test 7')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects characters from different scripts (Cyrillic, Greek) that look identical to Latin characters but 
                have different Unicode code points. Used for domain spoofing and bypassing filters.
              </Box>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Examples - Cross-Script Confusables:</Box>
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box variant="code">
                      <strong>Latin vs Cyrillic:</strong><br/>
                      • A (U+0041) vs А (U+0410)<br/>
                      • B (U+0042) vs В (U+0412)<br/>
                      • C (U+0043) vs С (U+0421)<br/>
                      • E (U+0045) vs Е (U+0415)<br/>
                      • o (U+006F) vs о (U+043E)<br/>
                      • p (U+0070) vs р (U+0440)
                    </Box>
                  </div>
                  <div>
                    <Box variant="code">
                      <strong>Latin vs Greek:</strong><br/>
                      • A (U+0041) vs Α (U+0391)<br/>
                      • B (U+0042) vs Β (U+0392)<br/>
                      • E (U+0045) vs Ε (U+0395)<br/>
                      • o (U+006F) vs ο (U+03BF)<br/>
                      • P (U+0050) vs Ρ (U+03A1)
                    </Box>
                  </div>
                </ColumnLayout>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Real-World Example:</Box>
                <Box variant="code">
                  Phishing Domain: pаypal.com<br/>
                  (Cyrillic 'а' U+0430 instead of Latin 'a' U+0061)<br/>
                  <br/>
                  Looks identical to: paypal.com<br/>
                  But resolves to: Different IP address<br/>
                  <br/>
                  Detection: Character at position 1 is Cyrillic, not Latin<br/>
                  Confidence: 95% (surrounded by Latin characters)
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">MEDIUM to HIGH</Badge> - Domain spoofing, phishing.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 8. Script Mixing */}
          <ExpandableSection 
            headerText="8. Script Mixing & Language Attacks" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('script-mixing', 'Test 8')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects words containing characters from multiple Unicode scripts (Latin + Cyrillic + Greek), which is 
                unusual and may indicate spoofing or filter bypass attempts.
              </Box>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example:</Box>
                <Box variant="code">
                  Word: "МісrоѕоÍt"<br/>
                  <br/>
                  Analysis:<br/>
                  • М (U+041C) - Cyrillic Capital M<br/>
                  • і (U+0456) - Cyrillic Small I<br/>
                  • с (U+0441) - Cyrillic Small C<br/>
                  • r (U+0072) - Latin Small R<br/>
                  • о (U+043E) - Cyrillic Small O<br/>
                  • ѕ (U+0455) - Cyrillic Small DZE<br/>
                  • о (U+043E) - Cyrillic Small O<br/>
                  • Í (U+00CD) - Latin Capital I with Acute<br/>
                  • t (U+0074) - Latin Small T<br/>
                  <br/>
                  Scripts detected: Cyrillic, Latin, Latin-Extended<br/>
                  Mixed words: 1<br/>
                  <br/>
                  Intended target: "Microsoft"
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to HIGH</Badge> - Spoofing, filter evasion.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 9. Language-Specific Attacks */}
          <ExpandableSection 
            headerText="9. Language-Specific Character Attacks" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('language-specific', 'Test 9')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects characters with language-specific case-folding or normalization rules that can bypass security filters.
              </Box>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Turkish İ/ı Attack:</strong><br/>
                    Username: "ADMİN" (Turkish İ, U+0130)<br/>
                    <br/>
                    Case folding:<br/>
                    • English: "ADMİN".toLowerCase() = "admi̇n"<br/>
                    • Turkish: "ADMİN".toLowerCase() = "admin"<br/>
                    <br/>
                    Risk: Bypasses case-insensitive "admin" filter in non-Turkish locale
                  </Box>
                  <Box variant="code">
                    <strong>2. German ß Attack:</strong><br/>
                    Email: user@ßank.com<br/>
                    <br/>
                    Normalization:<br/>
                    • ß → ss (in some contexts)<br/>
                    • ß → SS (uppercase)<br/>
                    <br/>
                    Risk: Validation inconsistencies, bypasses email filters
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="grey">LOW to MEDIUM</Badge> - Context-dependent bypass.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 10. Math Symbol Substitution */}
          <ExpandableSection 
            headerText="10. Mathematical Symbol Substitution" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('math-symbol-substitution', 'Test 10')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects use of Unicode mathematical alphanumeric symbols (bold, italic, script, fraktur, monospace) that 
                look like regular text but are different characters.
              </Box>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>Bold Math:</strong><br/>
                    Displayed: 𝐇𝐞𝐥𝐥𝐨 𝐖𝐨𝐫𝐥𝐝<br/>
                    Range: U+1D400-1D433<br/>
                    Original: Hello World<br/>
                    <br/>
                    Detected: 10 substitutions (category: bold)<br/>
                    𝐇 (U+1D407) → H, 𝐞 (U+1D41E) → e, ...
                  </Box>
                  <Box variant="code">
                    <strong>Script Math:</strong><br/>
                    Displayed: 𝒮𝓊𝓅ℯ𝓇 𝒮ℯ𝒸𝓇ℯ𝓉<br/>
                    Range: U+1D49C-1D4CF<br/>
                    Original: Super Secret<br/>
                    <br/>
                    Detected: 11 substitutions (category: script)
                  </Box>
                  <Box variant="code">
                    <strong>Circled/Enclosed:</strong><br/>
                    Displayed: ⓟⓐⓢⓢⓦⓞⓡⓓ<br/>
                    Range: U+24D0+<br/>
                    Original: password<br/>
                    <br/>
                    Detected: 8 substitutions (category: enclosed)
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="grey">LOW to MEDIUM</Badge> - Filter bypass, visual confusion.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 11. Unicode Normalization */}
          <ExpandableSection 
            headerText="11. Unicode Normalization Instability" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('normalization-instability', 'Test 11')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects text that changes when normalized to different Unicode forms (NFC, NFD, NFKC, NFKD), which can 
                cause validation bypasses or unexpected behavior.
              </Box>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example:</Box>
                <Box variant="code">
                  Original: "café" (é = U+00E9, 1 character)<br/>
                  NFD: "café" (e + ́ = U+0065 + U+0301, 2 characters)<br/>
                  <br/>
                  Original length: 4<br/>
                  NFD length: 5<br/>
                  <br/>
                  Changes detected:<br/>
                  • NFD normalization changes length<br/>
                  • Potential validation bypass<br/>
                  <br/>
                  Risk: Database lookups may fail, duplicate entries possible
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="grey">LOW to MEDIUM</Badge> - Validation bypass, data integrity.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 12. Control Character Exploits */}
          <ExpandableSection 
            headerText="12. Control Character Exploits" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('control-character-exploits', 'Test 12')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects ANSI escape sequences, backspace tricks, and other control characters that can manipulate terminal 
                output or hide malicious content.
              </Box>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '2px solid #f44336' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>⚠️ CRITICAL EXAMPLES:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Backspace Attack:</strong><br/>
                    Input: "admin\b\b\b\b\bguest"<br/>
                    (5 backspace characters U+0008)<br/>
                    <br/>
                    Displayed on screen: "guest"<br/>
                    Stored in database: "admin\b\b\b\b\bguest"<br/>
                    <br/>
                    Risk: User sees "guest", system processes "admin"<br/>
                    Result: Privilege escalation
                  </Box>
                  <Box variant="code">
                    <strong>2. ANSI Escape Sequence:</strong><br/>
                    Input: &quot;\x1b[31mDelete all files\x1b[0m&quot;<br/>
                    <br/>
                    Terminal display: <span style={{color: 'red'}}>Delete all files</span> (in red)<br/>
                    <br/>
                    More dangerous examples:<br/>
                    • \x1b[2J - Clear entire screen<br/>
                    • \x1b[H - Move cursor to home<br/>
                    • \x1b[?25l - Hide cursor<br/>
                    <br/>
                    Risk: Terminal manipulation, hiding commands
                  </Box>
                  <Box variant="code">
                    <strong>3. Carriage Return Attack:</strong><br/>
                    Input: "Downloading file...\rMalicious payload injected!"<br/>
                    <br/>
                    Display: Line overwrites itself<br/>
                    Result: "Malicious payload injected!" (overwrites progress message)<br/>
                    <br/>
                    Risk: Hiding malicious activity
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">HIGH to CRITICAL</Badge> - Terminal injection, privilege escalation.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 13. Pattern Detection */}
          <ExpandableSection 
            headerText="13. Repeating & Positional Patterns" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('positional-pattern', 'Test 13')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Analyzes the distribution and patterns of invisible characters to detect sophisticated encoding schemes.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detection Methods:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Repeating Intervals:</strong> Invisible chars at regular distances (e.g., every 8 characters)</Box>
                  <Box variant="p">• <strong>Start/End Clustering:</strong> Chars concentrated at text boundaries</Box>
                  <Box variant="p">• <strong>Positional Encoding:</strong> Location-based data encoding</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example - Repeating Pattern:</Box>
                <Box variant="code">
                  Text positions: [0, 8, 16, 24, 32, 40, 48, 56]<br/>
                  Interval: 8 characters<br/>
                  Confidence: 100% (all intervals match)<br/>
                  <br/>
                  Interpretation: Systematic encoding, likely metadata or tracking
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to HIGH</Badge> - Systematic data hiding.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 14. Combining Diacritics */}
          <ExpandableSection 
            headerText="14. Combining Diacritics Abuse (Zalgo Text)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('diacritics-abuse', 'Test 14')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects excessive combining diacritical marks on single characters, which can cause rendering issues, 
                buffer overflows, or text obfuscation.
              </Box>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Example:</Box>
                <Box variant="code">
                  Normal: "Hello"<br/>
                  <br/>
                  Zalgo text: "H̷̢̖̔e̴̯̿l̷̰̈l̸̰̓o̷̼̍"<br/>
                  (each character has 5-20 combining marks)<br/>
                  <br/>
                  Detected:<br/>
                  • Character 'H' at position 0: 8 diacritics<br/>
                  • Character 'e' at position 1: 12 diacritics<br/>
                  • Maximum diacritics on single char: 20<br/>
                  <br/>
                  Risks:<br/>
                  • Rendering engine crashes<br/>
                  • Buffer overflow exploits<br/>
                  • Screen reader disruption<br/>
                  • Text obfuscation
                </Box>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to HIGH</Badge> - DoS, rendering issues.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 15. Whitespace Abuse */}
          <ExpandableSection 
            headerText="15. Whitespace Abuse & Manipulation" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('whitespace-abuse', 'Test 15')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects unusual whitespace patterns including trailing/leading spaces, mixed space types, and tab mixing 
                used for text manipulation or filter bypassing.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detection Methods:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Trailing Whitespace:</strong> Spaces at end of lines (duplicate account creation)</Box>
                  <Box variant="p">• <strong>Leading Whitespace:</strong> Excessive spaces at start of lines</Box>
                  <Box variant="p">• <strong>Mixed Space Types:</strong> Em space, En space, figure space, etc.</Box>
                  <Box variant="p">• <strong>Tab Mixing:</strong> Tabs and spaces mixed in same text</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Trailing Whitespace Attack:</strong><br/>
                    Username: &quot;admin   &quot; (3 trailing spaces)<br/>
                    Looks like: &quot;admin&quot;<br/>
                    Actually: &quot;admin   &quot; (different from &quot;admin&quot;)<br/>
                    <br/>
                    Risk: Bypasses duplicate username check, creates shadow account
                  </Box>
                  <Box variant="code">
                    <strong>2. Mixed Space Types:</strong><br/>
                    Text: &quot;Hello World&quot; (uses Em space U+2003 instead of regular space)<br/>
                    Detection: 2 different space types detected<br/>
                    <br/>
                    Risk: Potential steganography or data hiding
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="grey">LOW to MEDIUM</Badge> - Depends on context and quantity.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 16. Character Width Anomalies */}
          <ExpandableSection 
            headerText="16. Character Width Anomalies (Full-width/Half-width)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('character-width-anomaly', 'Test 16')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects full-width and half-width character variations that appear identical to normal text but have 
                different Unicode code points. Frequently used in spoofing and filter bypass attacks.
              </Box>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '2px solid #f44336' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>⚠️ HIGH RISK EXAMPLES:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Username Spoofing:</strong><br/>
                    Full-width: Ａｄｍｉｎ (U+FF21, U+FF44, U+FF4D, U+FF49, U+FF4E)<br/>
                    Normal: Admin (U+0041, U+0064, U+006D, U+0069, U+006E)<br/>
                    <br/>
                    Visual: Both look identical<br/>
                    Database: Treated as different strings<br/>
                    <br/>
                    Attack: Create duplicate admin account, bypass username restrictions
                  </Box>
                  <Box variant="code">
                    <strong>2. Email Filter Bypass:</strong><br/>
                    Email: user@ｅｘａｍｐｌｅ.com (full-width domain)<br/>
                    Looks like: user@example.com<br/>
                    Actually: Different domain entirely<br/>
                    <br/>
                    Risk: Bypass email validation, domain spoofing
                  </Box>
                  <Box variant="code">
                    <strong>3. Length Restriction Bypass:</strong><br/>
                    Password limit: 8 characters<br/>
                    Input: ｐａｓｓ (4 full-width chars = 4 visual chars)<br/>
                    Stored: Takes up different byte length<br/>
                    <br/>
                    Risk: Bypass length validation, confuse validation logic
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">HIGH</Badge> - Frequently used in credential attacks and spoofing.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 17. Look-alike Digits */}
          <ExpandableSection 
            headerText="17. Look-alike Digits & Numbers" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('lookalike-digits', 'Test 17')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects Unicode characters that look like digits but aren&apos;t standard ASCII numbers, including 
                superscripts, subscripts, circled numbers, and Roman numerals.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detected Variants:</Box>
                <ColumnLayout columns={3} variant="text-grid">
                  <div>
                    <Box variant="p"><strong>Superscript:</strong> ¹²³⁴⁵⁶⁷⁸⁹⁰</Box>
                    <Box variant="p"><strong>Subscript:</strong> ₀₁₂₃₄₅₆₇₈₉</Box>
                  </div>
                  <div>
                    <Box variant="p"><strong>Circled:</strong> ①②③④⑤⑥⑦⑧⑨⑩</Box>
                    <Box variant="p"><strong>Parenthesized:</strong> ⑴⑵⑶⑷⑸</Box>
                  </div>
                  <div>
                    <Box variant="p"><strong>Roman:</strong> Ⅰ Ⅱ Ⅲ Ⅳ Ⅴ Ⅵ</Box>
                    <Box variant="p"><strong>Fractions:</strong> ½ ⅓ ¼ ⅔ ¾</Box>
                  </div>
                </ColumnLayout>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Attack Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Credit Card Validation Bypass:</strong><br/>
                    Input: 4532 ¹²³⁴ 5678 9012 (superscript middle digits)<br/>
                    Display: Looks like normal card number<br/>
                    Validation: Fails numeric check or passes incorrectly<br/>
                    <br/>
                    Risk: Bypass payment validation, cause processing errors
                  </Box>
                  <Box variant="code">
                    <strong>2. Age Verification Bypass:</strong><br/>
                    Age input: ⑱ (circled 18)<br/>
                    Looks like: 18<br/>
                    parseInt() result: NaN or fails<br/>
                    <br/>
                    Risk: Bypass age checks, validation errors
                  </Box>
                  <Box variant="code">
                    <strong>3. Phone Number Obfuscation:</strong><br/>
                    Phone: ⑴⑵⑶-⑷⑸⑹-⑺⑻⑼⑽<br/>
                    Visual: Looks like (123)-456-7890<br/>
                    Parsing: Completely different characters<br/>
                    <br/>
                    Risk: Bypass spam filters, avoid phone number detection
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM</Badge> - Financial/authentication implications.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 18. Quotation Mark Confusion */}
          <ExpandableSection 
            headerText="18. Quotation Mark Confusion (SQL Injection Risk)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('quotation-confusion', 'Test 18')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects non-standard quotation marks (typographic quotes, guillemets, primes) that can bypass 
                security filters, particularly SQL injection prevention.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detected Quote Types:</Box>
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box variant="p">• Typographic Double: " " „</Box>
                    <Box variant="p">• Typographic Single: ' ' ‚</Box>
                    <Box variant="p">• Guillemets: « » ‹ ›</Box>
                  </div>
                  <div>
                    <Box variant="p">• Grave/Acute: ` ´</Box>
                    <Box variant="p">• Primes: ′ ″</Box>
                    <Box variant="p">• Standard (expected): &quot; &apos;</Box>
                  </div>
                </ColumnLayout>
              </div>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '2px solid #f44336' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>⚠️ CRITICAL - SQL Injection Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Filter Bypass with Typographic Quotes:</strong><br/>
                    Blocked: SELECT * FROM users WHERE name = &quot;admin&quot;<br/>
                    Attack: SELECT * FROM users WHERE name = "admin"<br/>
                    (using U+201C and U+201D instead of U+0022)<br/>
                    <br/>
                    Filter: Looks for standard quotes (&quot;), misses typographic<br/>
                    Database: May interpret typographic quotes as valid<br/>
                    <br/>
                    Result: SQL injection bypasses string literal filter
                  </Box>
                  <Box variant="code">
                    <strong>2. Guillemet Injection:</strong><br/>
                    Input: username = «admin» OR 1=1--<br/>
                    Filter: Searches for &quot; or &apos; characters<br/>
                    Attack: Uses guillemets (« ») instead<br/>
                    <br/>
                    Risk: Bypass quote-based SQL injection filters
                  </Box>
                  <Box variant="code">
                    <strong>3. Prime Symbol Confusion:</strong><br/>
                    Filename: file′.txt (using prime symbol ′ instead of apostrophe)<br/>
                    Looks like: file&apos;.txt<br/>
                    Parsing: Different character entirely<br/>
                    <br/>
                    Risk: Bypass filename validation, path traversal protection
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">MEDIUM to HIGH</Badge> - SQL injection, filter bypass.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 19. Frequency Analysis */}
          <ExpandableSection 
            headerText="19. Frequency Analysis & Anomaly Detection" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('frequency-anomaly', 'Test 19')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Statistical analysis to detect unusual patterns suggesting encryption, encoding, or non-natural text 
                through entropy calculation and character distribution analysis.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detection Methods:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Shannon Entropy:</strong> Measures randomness (0-8 bits). High entropy (&gt;7) suggests encryption.</Box>
                  <Box variant="p">• <strong>Base64 Detection:</strong> Pattern matching with entropy analysis for Base64 encoding.</Box>
                  <Box variant="p">• <strong>Uniform Distribution:</strong> Unusually even character spread indicates steganography.</Box>
                  <Box variant="p">• <strong>Non-Textual Patterns:</strong> High digit ratio with low spaces suggests encoded data.</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Base64 Encoding Detection:</strong><br/>
                    Text: SGVsbG8gV29ybGQhIFRoaXMgaXMgYSB0ZXN0<br/>
                    Entropy: 5.8 bits<br/>
                    Pattern: Limited character set (A-Za-z0-9+/=)<br/>
                    <br/>
                    Detection: Base64 encoding with 90% confidence<br/>
                    Risk: Hidden payload, data exfiltration
                  </Box>
                  <Box variant="code">
                    <strong>2. High Entropy (Encryption):</strong><br/>
                    Text: 7aK9mP2vR8qN4wX1hB5jY3cT6fL0gD<br/>
                    Entropy: 7.2 bits (very high)<br/>
                    Distribution: Near-uniform across all characters<br/>
                    <br/>
                    Detection: Encrypted or compressed data<br/>
                    Risk: Hidden communication channel
                  </Box>
                  <Box variant="code">
                    <strong>3. Non-Textual Pattern:</strong><br/>
                    Text: 192168001001080808008192168001254<br/>
                    Digit ratio: 100% (no spaces or letters)<br/>
                    Entropy: 3.2 bits<br/>
                    <br/>
                    Detection: Likely encoded IP addresses or binary data<br/>
                    Risk: Network reconnaissance data
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to HIGH</Badge> - Data hiding, encrypted payloads.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 20. Domain-Specific Patterns */}
          <ExpandableSection 
            headerText="20. Domain-Specific Pattern Detection" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('domain-specific', 'Test 20')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Specialized detection for common data types (emails, IP addresses, phone numbers, credit cards) 
                that contain unusual Unicode characters for spoofing or filter bypass.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detected Patterns:</Box>
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box variant="p"><strong>Email Addresses:</strong> Full-width, Cyrillic in domains</Box>
                    <Box variant="p"><strong>IP Addresses:</strong> Non-standard separators (․ · ‧)</Box>
                  </div>
                  <div>
                    <Box variant="p"><strong>Phone Numbers:</strong> Unicode digit variants</Box>
                    <Box variant="p"><strong>Credit Cards:</strong> Superscript/subscript digits</Box>
                  </div>
                </ColumnLayout>
              </div>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '2px solid #f44336' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>⚠️ ATTACK EXAMPLES:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Email Phishing:</strong><br/>
                    Email: admin@ｐａｙｐａｌ.com<br/>
                    Issue: Full-width characters in domain (U+FF50+)<br/>
                    Looks like: admin@paypal.com<br/>
                    Actually: admin@ｐａｙｐａｌ.com (different domain)<br/>
                    <br/>
                    Risk: <Badge color="red">HIGH</Badge> - Domain spoofing, phishing attack
                  </Box>
                  <Box variant="code">
                    <strong>2. IP Address Manipulation:</strong><br/>
                    IP: 192․168․1․1<br/>
                    Issue: Middle dots (U+00B7) instead of periods<br/>
                    Normalized: 192.168.1.1<br/>
                    <br/>
                    Risk: <Badge color="red">HIGH</Badge> - Bypass URL filters and logging systems
                  </Box>
                  <Box variant="code">
                    <strong>3. Phone Number Obfuscation:</strong><br/>
                    Phone: ⑴⑵⑶-⑷⑸⑹-⑺⑻⑼⑽<br/>
                    Issue: Parenthesized digits (U+2474+)<br/>
                    Visual: (123)-456-7890<br/>
                    <br/>
                    Risk: <Badge>MEDIUM</Badge> - Bypass spam filters, avoid detection
                  </Box>
                  <Box variant="code">
                    <strong>4. Credit Card Bypass:</strong><br/>
                    Card: 4532 ¹²³⁴ 5678 9012<br/>
                    Issue: Superscript digits in middle section<br/>
                    <br/>
                    Risk: <Badge>MEDIUM</Badge> - Bypass payment validation, logging
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">LOW to HIGH</Badge> - Context dependent (IP/Email = HIGH).
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 21. String Length Manipulation */}
          <ExpandableSection 
            headerText="21. String Length Manipulation" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('length-manipulation', 'Test 21')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects characters that display as single visual units but store as multiple code points, 
                creating discrepancies between visual length and actual storage length.
              </Box>

              <div>
                <Box variant="awsui-key-label">Detection Criteria:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Grapheme Clusters:</strong> Visual units vs code point count</Box>
                  <Box variant="p">• <strong>ZWJ Emoji Sequences:</strong> Family emojis (👨‍👩‍👧‍👦) = 1 visual, 7 code points</Box>
                  <Box variant="p">• <strong>Combining Diacritics:</strong> Base + modifiers (é vs e+´)</Box>
                  <Box variant="p">• <strong>Variation Selectors:</strong> Appearance modifiers that add length</Box>
                  <Box variant="p">• <strong>Ratio Analysis:</strong> Suspicious if code points / graphemes &gt; 1.5x</Box>
                </SpaceBetween>
              </div>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Attack Examples:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Username Length Bypass:</strong><br/>
                    Length limit: 10 characters<br/>
                    Input: admin👨‍👩‍👧‍👦<br/>
                    <br/>
                    Visual length: 6 characters<br/>
                    Actual code points: 11 code points<br/>
                    Storage: 25 bytes (UTF-8)<br/>
                    <br/>
                    Bypass: Appears to pass 10-char limit but exceeds it<br/>
                    Risk: Database overflow, validation bypass
                  </Box>
                  <Box variant="code">
                    <strong>2. Combining Diacritics Abuse:</strong><br/>
                    Text: café (using e + combining acute accent)<br/>
                    Visual: 4 characters<br/>
                    Code points: 5 (c + a + f + e + ´)<br/>
                    <br/>
                    vs. café (using precomposed é)<br/>
                    Code points: 4 (c + a + f + é)<br/>
                    <br/>
                    Risk: Duplicate detection bypass, normalization issues
                  </Box>
                  <Box variant="code">
                    <strong>3. Password Validation Bypass:</strong><br/>
                    Min length: 8 characters<br/>
                    Input: pass👍🏻 (thumbs up with skin tone)<br/>
                    <br/>
                    Visual: 5 characters<br/>
                    Code points: 7 (p+a+s+s+👍+🏻 modifier)<br/>
                    <br/>
                    May pass visual check but fail/pass backend validation<br/>
                    Risk: Inconsistent validation behavior
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="grey">LOW to MEDIUM</Badge> - Validation bypass, buffer issues.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* ===== NEW 2024 UNICODE THREAT DETECTION METHODS ===== */}

          {/* 22. Private Use Area (PUA) Detection */}
          <ExpandableSection 
            headerText="22. Private Use Area (PUA) Character Detection (2024)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('pua-detection', 'Test 22')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects Private Use Area characters (U+E000–U+F8FF, U+F0000–U+FFFFD, U+100000–U+10FFFD) - 137,468 characters 
                with no standardized meaning that can be used for steganography, icon fonts, or data hiding.
              </Box>

              <div style={{ backgroundColor: '#fff3cd', padding: '12px', borderRadius: '4px', border: '2px solid #ff9800' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>🆕 2024 THREAT - Private Use Area Abuse:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Icon Font Steganography:</strong><br/>
                    Text: Contains Font Awesome (U+F000–U+F2FF) characters embedded in natural text<br/>
                    Density: 3% PUA characters<br/>
                    Legitimate: Font rendering context<br/>
                    <br/>
                    Malicious: High PUA density in email/document suggests hidden data encoding
                  </Box>
                  <Box variant="code">
                    <strong>2. Hidden Data Channel:</strong><br/>
                    Detected: 12% PUA character density in plaintext<br/>
                    Pattern: Sequential PUA characters (U+E001, U+E002, U+E003...)<br/>
                    <br/>
                    Attack: Each PUA character encodes a byte of hidden data<br/>
                    Risk: Data exfiltration, covert communication channels
                  </Box>
                  <Box variant="code">
                    <strong>3. Fingerprinting & Watermarking:</strong><br/>
                    Detection: Sparse PUA characters (0.1% density) in AI-generated content<br/>
                    Pattern: 8-character PUA sequence<br/>
                    <br/>
                    Attack: Unique identifier embedded for user tracking<br/>
                    Risk: User de-anonymization, copyright tracking
                  </Box>
                </SpaceBetween>
              </div>

              <div>
                <Box variant="awsui-key-label">Risk Assessment:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>LOW:</strong> &lt;1% density + HTML/CSS context (likely icon fonts)</Box>
                  <Box variant="p">• <strong>MEDIUM:</strong> 1-5% density + mixed with normal text</Box>
                  <Box variant="p">• <strong>HIGH:</strong> 5-20% density + isolated from content</Box>
                  <Box variant="p">• <strong>CRITICAL:</strong> &gt;20% density + sequential patterns (definite steganography)</Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="blue">MEDIUM to CRITICAL</Badge> - Steganography, data hiding, fingerprinting.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 23. Fullwidth Exploitation */}
          <ExpandableSection 
            headerText="23. Fullwidth Character Exploitation (CVE-2024-4577)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('fullwidth-exploitation', 'Test 23')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects fullwidth character abuse (U+FF00–U+FFEF) that normalize to dangerous ASCII characters after validation, 
                bypassing security checks through "Normalization-After-Validation" vulnerabilities.
              </Box>

              <div style={{ backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', border: '2px solid #f44336' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>🚨 CRITICAL - CVE-2024-4577 Attack Pattern:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Path Traversal via Fullwidth:</strong><br/>
                    Input: file．．/．．/etc/passwd<br/>
                    (U+FF0E fullwidth dots instead of normal dots U+002E)<br/>
                    <br/>
                    Validation: Passes filter checking for "../" (normalized dots not detected)<br/>
                    Processing: Normalizes to ../ then executes path traversal<br/>
                    <br/>
                    Vulnerable: PHP-CGI, Apache, file handlers<br/>
                    CVE: CVE-2024-4577
                  </Box>
                  <Box variant="code">
                    <strong>2. HTML Injection via Fullwidth Brackets:</strong><br/>
                    Input: ＜script＞alert('XSS')＜/script＞<br/>
                    (U+FF1C/U+FF1E fullwidth brackets instead of U+003C/U+003E)<br/>
                    <br/>
                    Validation: HTML sanitizer doesn't recognize fullwidth brackets<br/>
                    Rendering: Browser normalizes and executes script<br/>
                    <br/>
                    CVE: CVE-2024-34078
                  </Box>
                  <Box variant="code">
                    <strong>3. Backslash Escape Bypass:</strong><br/>
                    Input: C：＼Users＼Admin＼secret.txt<br/>
                    (U+FF3C fullwidth backslash instead of U+005C)<br/>
                    <br/>
                    Detection: Appears safe (no ASCII backslash detected)<br/>
                    Processing: Path handler normalizes and accepts it<br/>
                    Risk: Directory traversal, file access bypass
                  </Box>
                </SpaceBetween>
              </div>

              <div>
                <Box variant="awsui-key-label">Critical Transformation Rules:</Box>
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <Box variant="code">
                      U+FF0E (．) → . (period)<br/>
                      U+FF3C (＼) → \ (backslash)<br/>
                      U+FF1C (＜) → &lt; (less-than)<br/>
                      U+FF1E (＞) → &gt; (greater-than)
                    </Box>
                  </div>
                  <div>
                    <Box variant="code">
                      U+FF0F (／) → / (slash)<br/>
                      U+FF1A (：) → : (colon)<br/>
                      U+FF0A (＊) → * (asterisk)<br/>
                      U+FF1F (？) → ? (question)
                    </Box>
                  </div>
                </ColumnLayout>
              </div>

              <Box variant="p">
                <strong>KEY LESSON:</strong> <Badge color="red">CRITICAL</Badge> - Always normalize text <strong>BEFORE</strong> validation, never after!
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 24. Tag Block Anomalies */}
          <ExpandableSection 
            headerText="24. Tag Block Character Smuggling (U+E0000–U+E007F)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('tag-block-smuggling', 'Test 24')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects Tag Block characters (U+E0000–U+E007F) used for LLM prompt injection, safety filter bypass, and 
                steganography in AI systems (2024 emerging threat).
              </Box>

              <div style={{ backgroundColor: '#f0e6ff', padding: '12px', borderRadius: '4px', border: '2px solid #9c27b0' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>🆕 2024 LLM ATTACK - Tag Block Smuggling:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. LLM Prompt Injection:</strong><br/>
                    Input: "Summarize this: [benign text]‮[tag block chars][malicious instruction]‮"<br/>
                    <br/>
                    Detection: Invisible tag block characters hide malicious prompt<br/>
                    LLM Response: Executes hidden instruction instead of summarizing<br/>
                    <br/>
                    Risk: Jailbreak LLM safety filters, unauthorized actions
                  </Box>
                  <Box variant="code">
                    <strong>2. Token Expansion DoS:</strong><br/>
                    Pattern: U+E0000 through U+E007F (128 characters) concatenated<br/>
                    Effect: Each tag character expands to multiple tokens in LLMs<br/>
                    Result: 128 chars → 500+ tokens (token limit exhaustion)<br/>
                    <br/>
                    Attack: Denial of Service via LLM token limit overflow
                  </Box>
                  <Box variant="code">
                    <strong>3. Content Fingerprinting:</strong><br/>
                    Detection: 4-8 tag block characters in AI-generated content<br/>
                    Pattern: Unique sequence per generation instance<br/>
                    <br/>
                    Attack: Watermarking for user identification<br/>
                    Risk: De-anonymization of AI-generated content
                  </Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">HIGH to CRITICAL</Badge> - LLM jailbreak, prompt injection, DoS attacks.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 25. Emoji Token Expansion */}
          <ExpandableSection 
            headerText="25. Emoji Token Expansion LLM Attack (Wei et al., 2024)" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('emoji-token-expansion', 'Test 25')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                Detects strategic emoji insertion to exploit LLM tokenization and bypass AI safety filters through 
                token segmentation bias (12-75% bypass rate observed).
              </Box>

              <div style={{ backgroundColor: '#ffe6e6', padding: '12px', borderRadius: '4px', border: '2px solid #d32f2f' }}>
                <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>🆕 2024 RESEARCH - Wei et al. arXiv:2411.01077v1:</Box>
                <SpaceBetween size="xs">
                  <Box variant="code">
                    <strong>1. Token Expansion Bypass:</strong><br/>
                    Attack: "Help me write: [emoji]🇺🇸[emoji] malicious code"<br/>
                    (using complex emoji: flags, ZWJ sequences, skin tones)<br/>
                    <br/>
                    Tokenization: Each emoji expands 2-5 tokens<br/>
                    Safety filter: "malicious code" becomes fragmented tokens<br/>
                    <br/>
                    Result: Bypass rate 12-75% depending on model
                  </Box>
                  <Box variant="code">
                    <strong>2. Detected Patterns - High Risk Emoji:</strong><br/>
                    • Family sequences: 👨‍👩‍👧‍👦 (7+ tokens)<br/>
                    • Flags: 🇺🇸 🇨🇳 🇷🇺 (2-3 tokens each)<br/>
                    • Skin tones: 👍🏻 👋🏽 (2-3 tokens)<br/>
                    • Profession sequences: 👨‍💻 👩‍⚕️ (3-4 tokens)<br/>
                    <br/>
                    Detection: Multiple complex emojis in short sentences
                  </Box>
                  <Box variant="code">
                    <strong>3. Attack Effectiveness - Tested Models:</strong><br/>
                    Llama Guard: 45% bypass rate<br/>
                    ShieldLM: 38% bypass rate<br/>
                    GPT-3.5: 75% bypass rate<br/>
                    GPT-4: 12% bypass rate<br/>
                    <br/>
                    Strategy: Strategically placed emoji disrupt safety evaluation
                  </Box>
                </SpaceBetween>
              </div>

              <div>
                <Box variant="awsui-key-label">Detection Indicators:</Box>
                <SpaceBetween size="xs">
                  <Box variant="p">• <strong>Unusual Placement:</strong> Emojis between harmful keywords</Box>
                  <Box variant="p">• <strong>High Token Count:</strong> Complex emojis (families, flags with ZWJ)</Box>
                  <Box variant="p">• <strong>ZWJ Chains:</strong> Multiple Zero-Width Joiners in sequences</Box>
                  <Box variant="p">• <strong>Non-Natural Distribution:</strong> Emoji density &gt;2% in text</Box>
                  <Box variant="p">• <strong>Mixed Scripts:</strong> Emoji + harmful keywords + Cyrillic mixing</Box>
                </SpaceBetween>
              </div>

              <Box variant="p">
                <strong>Risk Level:</strong> <Badge color="red">HIGH to CRITICAL</Badge> - LLM safety bypass, emerging 2024 attack vector.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 26. PHASE 1 - LLM GPT-4 Jailbreak */}
          <ExpandableSection 
            headerText="26. LLM GPT-4 Jailbreak - Token Expansion & Tag Block Smuggling" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('llm-gpt4-jailbreak', 'Test 26')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="red">CRITICAL PHASE 1</Badge> Detects LLM jailbreak attacks using tag block characters and token expansion to bypass GPT-4 safety systems.
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> Tag block smuggling (U+E0000-E007F), token expansion with complex emojis, combined attacks</Box>
              <Box variant="p"><strong>Risk Level:</strong> <Badge color="red">CRITICAL</Badge> - Bypass rates: GPT-4 12%, GPT-3.5 75%, Claude 45%, Llama 68%</Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 27. Combined BiDi + Homoglyph */}
          <ExpandableSection 
            headerText="27. Combined BiDi Override + Homoglyph Attack" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('combined-bidi-homoglyph', 'Test 27')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="red">CRITICAL PHASE 1</Badge> Sophisticated phishing combining bidirectional override with homoglyph characters for perfect domain spoofing.
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> BiDi (RLO U+202E) + Cyrillic homoglyphs for visual domain reversal, credential harvesting</Box>
              <Box variant="p"><strong>Real-World Example:</strong> Facebook CEO phishing attack using Cyrillic characters</Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 28. Combined Steganography + Normalization */}
          <ExpandableSection 
            headerText="28. Combined Steganography + Normalization Evasion" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('combined-steganography-normalization', 'Test 28')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="red">CRITICAL PHASE 1</Badge> Multi-layer steganography exploiting Unicode normalization timing for 99% detection evasion.
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> 7 distinct vectors including combining marks, variation selectors, multi-layer 5-vector stacking</Box>
              <Box variant="p"><strong>Key Insight:</strong> Normalize FIRST (before validation) = Defense; normalize LAST (after validation) = Vulnerability</Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 29. NTFS ADS Exploitation */}
          <ExpandableSection 
            headerText="29. NTFS Alternate Data Stream (ADS) Exploitation" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('ntfs-ads-exploitation', 'Test 29')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="red">HIGH PHASE 1</Badge> Windows-specific attack hiding malware in NTFS alternate data streams (file:stream syntax).
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> 4 vectors including malware hiding, data exfiltration, persistence, file integrity bypass</Box>
              <Box variant="p"><strong>Example:</strong> invoice.pdf:malware.exe - PDF looks clean, malware hidden in alternate stream</Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 30. Grapheme Cluster & ZWJ Abuse */}
          <ExpandableSection 
            headerText="30. Grapheme Cluster & ZWJ Sequence Abuse" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('grapheme-cluster-abuse', 'Test 30')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="red">HIGH-CRITICAL PHASE 1</Badge> Modern Unicode 15 grapheme attacks using complex ZWJ sequences.
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> 6 vectors including ZWJ length bypass, token expansion, variation selector abuse, emoji flag steganography</Box>
              <Box variant="p"><strong>Data Capacity:</strong> 10 hearts = 80 bytes hidden with 4-8 bits per character via selectors</Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 31. RTL Shaping Attacks */}
          <ExpandableSection 
            headerText="31. RTL Text Shaping - Arabic/Hebrew Rendering Attack" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('rtl-shaping-attack', 'Test 31')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="blue">HIGH PHASE 2</Badge> Right-to-Left text rendering exploitation for phishing and filter bypass.
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> 6 vectors including visual/logical order confusion, git repo poisoning, source code obfuscation</Box>
              <Box variant="p"><strong>Timeline:</strong> Real attacks: 2018 GitHub RTL, 2019 PayPal domain spoofing, 2023-2024 repo injection</Box>
            </SpaceBetween>
          </ExpandableSection>

          {/* 32. Variation Selector Steganography */}
          <ExpandableSection 
            headerText="32. Variation Selector Steganography - Data Hiding" 
            variant="container"
            headerActions={
              <Button onClick={() => copyToClipboard('variation-selector-steganography', 'Test 32')} iconName="copy">
                Try Sample
              </Button>
            }
          >
            <SpaceBetween size="m">
              <Box variant="p">
                <Badge color="blue">HIGH-CRITICAL PHASE 2</Badge> Variation selector (U+FE00–U+FE0F) clustering for steganographic data encoding.
              </Box>
              <Box variant="p"><strong>Attack Vectors:</strong> 7 vectors including binary encoding, AI fingerprinting, email tracking, hash collision, emoji clustering</Box>
              <Box variant="p"><strong>Steganographic Capacity:</strong> 4-8 bits per selector; 10 hearts = 80 bytes invisible data encoded</Box>
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Risk Assessment</Header>}>
        <SpaceBetween size="m">
          <Box variant="p">
            The tool calculates an overall risk score (0-100) and threat level based on:
          </Box>

          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="h4">Threat Levels:</Box>
              <SpaceBetween size="xs">
                <Box variant="p"><Badge color="grey">LOW</Badge> Score 0-24: Minor issues, likely benign</Box>
                <Box variant="p"><Badge>MEDIUM</Badge> Score 25-49: Suspicious patterns detected</Box>
                <Box variant="p"><Badge color="blue">HIGH</Badge> Score 50-74: Likely malicious intent</Box>
                <Box variant="p"><Badge color="red">CRITICAL</Badge> Score 75-100: Active threat detected</Box>
              </SpaceBetween>
            </div>
            <div>
              <Box variant="h4">Risk Factors:</Box>
              <SpaceBetween size="xs">
                <Box variant="p">• Density of invisible characters</Box>
                <Box variant="p">• Character category diversity</Box>
                <Box variant="p">• Pattern complexity</Box>
                <Box variant="p">• Known attack signatures</Box>
                <Box variant="p">• Context and positioning</Box>
              </SpaceBetween>
            </div>
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      <Container header={<Header variant="h2">Best Practices</Header>}>
        <SpaceBetween size="m">
          <div>
            <Box variant="h4">For Users:</Box>
            <SpaceBetween size="xs">
              <Box variant="p">✓ Paste suspicious text before clicking links or opening files</Box>
              <Box variant="p">✓ Verify domain names character-by-character</Box>
              <Box variant="p">✓ Be cautious of files with unusual names</Box>
              <Box variant="p">✓ Remove invisible characters before submitting forms</Box>
            </SpaceBetween>
          </div>

          <div>
            <Box variant="h4">For Developers:</Box>
            <SpaceBetween size="xs">
              <Box variant="p">✓ Validate and sanitize all user input</Box>
              <Box variant="p">✓ Normalize Unicode text (NFC) before processing</Box>
              <Box variant="p">✓ Strip invisible characters from security-sensitive fields</Box>
              <Box variant="p">✓ Implement homoglyph detection for domains</Box>
              <Box variant="p">✓ Check for BiDi override characters in filenames</Box>
              <Box variant="p">✓ Use allowlists for character sets when possible</Box>
            </SpaceBetween>
          </div>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
    </>
  );
};

